import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';

import { ActionsList } from './ActionsList';
import { BlockingReasonList } from './BlockingReasonList';
import { BookingSessionDescription } from './BookingSessionDescription';
import { BookingSessionDetails } from './BookingSessionDetails';
import { BlockingReasonAction } from './_types';
import { CheckinBlockerSessionDetails, CheckinBlockerUser } from './_types';

export function SessionContent({
  users,
  actions,
  getBlockerActions,
  sessionDetails,
  fillAvailableSpace = false,
}: {
  users: CheckinBlockerUser[];
  actions?: BlockingReasonAction[];
  getBlockerActions?: (
    user: CheckinBlockerUser,
    blocker: CheckinBlockerUser['blockers'][number],
    index: number
  ) => BlockingReasonAction[] | undefined;
  sessionDetails: CheckinBlockerSessionDetails;
  fillAvailableSpace?: boolean;
}) {
  return (
    <Stack
      spacing={3}
      sx={{
        width: '100%',
        ...(fillAvailableSpace && {
          flex: 1,
          minHeight: 0,
          justifyContent: 'space-between',
        }),
      }}
    >
      {/*
        Use `sm` (600px), not `md` (900px): the booking embed iframe is ~650px wide,
        so md media queries never match inside the iframe viewport.
      */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={3}
        alignItems="stretch"
        sx={{
          width: '100%',
          ...(fillAvailableSpace && {
            flex: 1,
            minHeight: 0,
          }),
        }}
      >
        {/* Left: description + reason (desktop). Mobile: reason before description. */}
        <Stack
          spacing={2}
          sx={{
            width: { xs: '100%', sm: '50%' },
            flex: { sm: '1 1 50%' },
            minWidth: 0,
            pr: { sm: 1.5 },
          }}
        >
          <Box sx={{ order: { xs: 2, sm: 1 }, width: '100%' }}>
            <BookingSessionDescription sessionDetails={sessionDetails} />
          </Box>

          {users.map((user, index) => (
            <Stack
              key={
                user.id != null && user.id !== ''
                  ? user.id
                  : `checkin-blocker-${index}`
              }
              spacing={2}
              sx={{ order: { xs: 1, sm: 2 }, width: '100%' }}
            >
              <BlockingReasonList
                blockers={user.blockers}
                getBlockerActions={(blocker, blockerIndex) =>
                  getBlockerActions?.(user, blocker, blockerIndex)
                }
                userId={user.id ?? ''}
                variant="booking"
              />
            </Stack>
          ))}
        </Stack>

        {/* Right: Book for, staff, location, when, who's going */}
        <Stack
          sx={{
            width: { xs: '100%', sm: '50%' },
            flex: { sm: '1 1 50%' },
            minWidth: 0,
            pl: { sm: 1.5 },
          }}
        >
          <BookingSessionDetails
            sessionDetails={sessionDetails}
            bookForUser={users[0]}
          />
        </Stack>
      </Stack>

      {!!actions && (
        <Box sx={fillAvailableSpace ? { mt: 'auto', pt: 1 } : undefined}>
          <ActionsList actions={actions} />
        </Box>
      )}
    </Stack>
  );
}
